import { Controller } from '@hotwired/stimulus';
import { focusSibling } from '../lib/focus';

// Menu déroulant Bootstrap 3 : classe `open` sur le conteneur (le CSS affiche
// `.open > .dropdown-menu`), `aria-expanded` sur le bouton. Une fois ouvert,
// tout clic le ferme — y compris sur une entrée : un lien navigue, un bouton
// agit sur place et rend le focus au déclencheur —, ainsi qu'Échap et la sortie
// du focus. Les flèches déplacent le focus entre les entrées du menu.
//
// Actions attendues sur le conteneur :
//   click@window->dropdown#closeOnClickAway
//   focusin@window->dropdown#closeOnFocusAway
//   keydown.esc@window->dropdown#closeOnEscape
//   keydown.up->dropdown#focusPrevious keydown.down->dropdown#focusNext
// et sur le bouton (cible `button`) : dropdown#toggle.
export default class DropdownController extends Controller<HTMLElement> {
  static targets = ['button'];

  declare readonly buttonTarget: HTMLElement;

  toggle(event: Event): void {
    event.preventDefault();

    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  close(): void {
    this.element.classList.remove('open');
    this.buttonTarget.setAttribute('aria-expanded', 'false');
  }

  // Le clic sur le bouton est déjà traité par `toggle`.
  closeOnClickAway(event: Event): void {
    const target = event.target as Element;

    if (!this.isOpen || this.buttonTarget.contains(target)) return;

    this.close();
    if (this.element.contains(target) && target.closest('button')) {
      this.buttonTarget.focus();
    }
  }

  closeOnFocusAway(event: FocusEvent): void {
    if (this.isOpen && !this.element.contains(event.target as Node)) {
      this.close();
    }
  }

  closeOnEscape(): void {
    if (!this.isOpen) return;

    this.close();
    this.buttonTarget.focus();
  }

  focusPrevious(event: Event): void {
    this.moveFocus(event, -1);
  }

  focusNext(event: Event): void {
    this.moveFocus(event, 1);
  }

  private open(): void {
    this.element.classList.add('open');
    this.buttonTarget.setAttribute('aria-expanded', 'true');
    // Safari et Firefox ne focalisent pas un bouton cliqué : sans cela, les
    // flèches et la fermeture à la sortie du focus resteraient sans effet.
    this.buttonTarget.focus();
  }

  private moveFocus(event: Event, offset: number): void {
    if (!this.isOpen) return;

    event.preventDefault();
    focusSibling(this.menuEntries, offset);
  }

  private get menuEntries(): HTMLElement[] {
    return Array.from(
      this.element.querySelectorAll<HTMLElement>(
        '.dropdown-menu > li > a, .dropdown-menu > li > button'
      )
    );
  }

  private get isOpen(): boolean {
    return this.element.classList.contains('open');
  }
}
